import { configureStore } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';

import { getSessionState, setSessionState } from '@bay-ui/BayPolotnoEditor/utils';

import {
	getTemplateState,
	setTemplateState,
	resetTemplateState,
	getDefaultTemplateState,
	sanitizeTemplateState,
} from './slices/templateSlice';
import {
	getTemplatesState,
	setTemplatesState,
	updateTemplatesState,
	resetTemplatesState,
} from './slices/templatesSlice';
import {
	getHeadlineState,
	setHeadlineState,
	resetHeadlineState,
	getDefaultHeadlineState,
} from './slices/headlineSlice';
import { getNavState, setNavState, resetNavState } from './slices/navSlice';
import {
	getNotificationState,
	setNotificationState,
	resetNotificationState,
} from './slices/notificationSlice';
import { getUserState, setUserState, resetUserState } from './slices/userSlice';
import {
	getActingAsState,
	setActingAsState,
	resetActingAsState,
} from './slices/actingAsSlice';
import {
	getModalState,
	setModalState,
	resetModalState,
} from './slices/modalSlice';
import {
	getTagsState,
	setTagsLoading,
	setTagSetsState,
	setKeywordsState,
	addTagToTagSet,
	addKeyword,
	updateTagInTagSet,
	updateKeyword,
	deleteTagFromTagSet,
	deleteKeyword,
	setTagsError,
	resetTagsState,
} from './slices/tagsSlice';

import reducers from './reducers';

const GLOBAL_STATE_SCHEMA_VERSION = 3;

const createPersistedGlobalState = ({ template, headline, notification }) => {
	return {
		schemaVersion: GLOBAL_STATE_SCHEMA_VERSION,
		template,
		headline,
		notification,
	};
};

const migratePersistedGlobalState = (persistedState) => {
	const nextTemplate = sanitizeTemplateState(
		persistedState?.template || getDefaultTemplateState('Post'),
	);

	return createPersistedGlobalState({
		template: nextTemplate,
		headline: persistedState?.headline || getDefaultHeadlineState(),
		notification: persistedState?.notification || null,
	});
};

//MIDDLEWARE
const localStorageMiddleware = ({ getState }) => {
	return (next) => (action) => {
		const result = next(action);
		//DO NOT MUTATE
		const { template, headline, notification } = getState();
		//OVERRIDES
		//console.log('rehydrate');
		if (
			window.location.pathname === '/login' ||
			window.location.pathname === '/logout'
		) {
			//setSessionState('globalState', { template: getDefaultTemplateState(), headline: getDefaultHeadlineState(), notification, user, actingAs });
			setSessionState(
				'globalState',
				createPersistedGlobalState({
					template: getDefaultTemplateState('Post'),
					headline: getDefaultHeadlineState(),
					notification,
				}),
			);
		} else {
			//setSessionState('globalState', { template, headline, notification, user, actingAs });
			setSessionState(
				'globalState',
				createPersistedGlobalState({ template, headline, notification }),
			);
		}
		return result;
	};
};

const reHydrateStore = () => {
	if (sessionStorage.getItem('globalState') !== null) {
		const persistedState = getSessionState('globalState');
		const migratedState = migratePersistedGlobalState(persistedState);

		if (
			!persistedState ||
			persistedState.schemaVersion !== GLOBAL_STATE_SCHEMA_VERSION
		) {
			setSessionState('globalState', migratedState);
		}

		const { schemaVersion, ...state } = migratedState;
		// Don't rehydrate tags - always start fresh
		if (state && state.tags) {
			delete state.tags;
		}
		if (state && state.views) {
			delete state.views;
		}
		return state;
	}
};

const store = configureStore({
	devTools: true,
	reducer: reducers,
	preloadedState: reHydrateStore(),
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware().concat(localStorageMiddleware),
});

const globalState = {
	//redux
	useDispatch,
	store,
	//template
	useTemplateState: () => {
		let templateState = useSelector(getTemplateState);

		return templateState;
	},
	getDefaultTemplateState,
	setTemplateState,
	resetTemplateState,

	//templates
	useTemplatesState: () => {
		let templatesState = useSelector(getTemplatesState);

		return templatesState;
	},
	setTemplatesState,
	updateTemplatesState,
	resetTemplatesState,

	//templates
	useHeadlineState: () => {
		let templateState = useSelector(getHeadlineState);

		return templateState;
	},
	getDefaultHeadlineState,
	setHeadlineState,
	resetHeadlineState,

	//nav
	useNavState: () => {
		let navState = useSelector(getNavState);

		return navState;
	},
	setNavState,
	resetNavState,

	//notifications
	useNotificationState: () => {
		let notificationState = useSelector(getNotificationState);

		return notificationState;
	},
	setNotificationState,
	resetNotificationState,

	//user
	useUserState: () => {
		let userState = useSelector(getUserState);

		return userState;
	},
	setUserState,
	resetUserState,

	//Acting As
	useActingAsState: () => {
		let actingAsState = useSelector(getActingAsState);

		return actingAsState;
	},
	setActingAsState,
	resetActingAsState,

	//modal
	useModalState: () => {
		let modalState = useSelector(getModalState);

		return modalState;
	},
	setModalState,
	resetModalState,

	//tags
	useTagsState: () => {
		let tagsState = useSelector(getTagsState);

		return tagsState;
	},
	setTagsLoading,
	setTagSetsState,
	setKeywordsState,
	addTagToTagSet,
	addKeyword,
	updateTagInTagSet,
	updateKeyword,
	deleteTagFromTagSet,
	deleteKeyword,
	setTagsError,
	resetTagsState,
};

export default globalState;
